import { useEffect, useRef } from 'react';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { Tray, useAlert } from '@frontend/design-system';
import { ExtendedCallRoute } from '../types';
import { CallRouteTabs, NumbersPanelTabs } from './call-route-tabs';
import { DeviceExtensionRouteNumbers } from './device-extension-route-numbers';

type Props = {
  initialTab?: NumbersPanelTabs;
  callRoute: ExtendedCallRoute;
  hasChildLocations: boolean;
  isDeviceExtensionRoute?: boolean;
  onEditPhoneNumbers: (callRouteId: string) => void;
  onEditExtensions: (callRouteId: string) => void;
};

export const NumbersPanel = ({
  callRoute,
  initialTab,
  hasChildLocations,
  isDeviceExtensionRoute = false,
  onEditPhoneNumbers,
  onEditExtensions,
}: Props) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();

  // Keep the latest callRoute in a ref to use in async callbacks (for "undo" actions)
  const callRouteRef = useRef(callRoute);

  useEffect(() => {
    callRouteRef.current = callRoute;
  }, [callRoute]);

  const { mutate: updateCallRoutePhoneNumbers, isLoading: updateCallRoutePhoneNumbersIsLoading } =
    CallRouteV1.Mutations.useUpdatePhoneNumbersMutation();

  const { mutate: updateCallRouteExtensions, isLoading: updateCallRouteExtensionsIsLoading } =
    CallRouteV1.Mutations.useUpdateExtensionsMutation();

  const handleRemovePhoneNumber = (phoneNumberId: string) => {
    const prevPhoneNumberIds = callRoute.phoneNumbers.map((phoneNumber) => phoneNumber.id);
    const newPhoneNumberIds = prevPhoneNumberIds.filter((id) => id !== phoneNumberId);

    updateCallRoutePhoneNumbers(
      {
        callRouteId: callRoute.callRouteId,
        phoneNumberIds: newPhoneNumberIds,
      },
      {
        onSuccess: () => {
          alerts.success({
            message: t('Phone Number removed successfully.'),
            action: {
              label: t('Undo'),
              onClick: () => handleUndoRemovePhoneNumber(phoneNumberId),
            },
          });
        },
        onError: () => {
          alerts.error(t('Failed to remove phone number.'));
        },
      }
    );
  };

  const handleUndoRemovePhoneNumber = (phoneNumberId: string) => {
    const latestCallRoute = callRouteRef.current;
    if (!latestCallRoute) return;

    const phoneNumberIds = [
      // We need to get the phone numbers from the latest version of the callRoute prop as
      // as opposed to the value stored due to lexical scoping in the async method so that
      // when we do the update to add the phone number back, we use the latest value of
      // phone numbers so that we avoid updating with older phone numbers that may have been
      // removed since. This can happen if the user removes multiple phone numbers in quick
      // succession and then tries to undo the removal of a phone number that was removed.
      ...(latestCallRoute?.phoneNumbers.map((phoneNumber) => phoneNumber.id) ?? []),
      phoneNumberId,
    ];
    updateCallRoutePhoneNumbers(
      {
        callRouteId: callRoute.callRouteId,
        phoneNumberIds,
      },
      {
        onSuccess: () => {
          alerts.success(t('Phone Number restored successfully.'));
        },
        onError: () => {
          alerts.error(t('Failed to restore phone number.'));
        },
      }
    );
  };

  const handleRemoveExtension = (extensionToRemove: number) => {
    const prevExtensions = callRoute.extensionNumbers;
    const newExtensions = prevExtensions.filter((ext) => ext !== extensionToRemove);

    updateCallRouteExtensions(
      {
        callRouteId: callRoute.callRouteId,
        extensionNumbers: newExtensions,
      },
      {
        onSuccess: () => {
          alerts.success({
            message: t('Extension removed successfully.'),
            action: {
              label: t('Undo'),
              onClick: () => handleUndoRemoveExtension(extensionToRemove),
            },
          });
        },
        onError: () => {
          alerts.error(t('Failed to remove extension.'));
        },
      }
    );
  };

  const handleUndoRemoveExtension = (removedExtension: number) => {
    const latestCallRoute = callRouteRef.current;
    if (!latestCallRoute) return;

    const extensionNumbers = [...(latestCallRoute?.extensionNumbers ?? []), removedExtension];
    updateCallRouteExtensions(
      {
        callRouteId: callRoute.callRouteId,
        extensionNumbers,
      },
      {
        onSuccess: () => {
          alerts.success(t('Extension restored successfully.'));
        },
        onError: () => {
          alerts.error(t('Failed to restore extension.'));
        },
      }
    );
  };

  return (
    <Tray.Body>
      {isDeviceExtensionRoute ? (
        <DeviceExtensionRouteNumbers
          callRoute={callRoute}
          hasChildLocations={hasChildLocations}
          isLoading={updateCallRouteExtensionsIsLoading || updateCallRoutePhoneNumbersIsLoading}
          onEditPhoneNumbers={onEditPhoneNumbers}
          onRemovePhoneNumber={handleRemovePhoneNumber}
        />
      ) : (
        <CallRouteTabs
          initialTab={initialTab}
          callRoute={callRoute}
          hasChildLocations={hasChildLocations}
          isLoading={updateCallRouteExtensionsIsLoading || updateCallRoutePhoneNumbersIsLoading}
          onEditPhoneNumbers={onEditPhoneNumbers}
          onEditExtensions={onEditExtensions}
          onRemovePhoneNumber={handleRemovePhoneNumber}
          onRemoveExtension={handleRemoveExtension}
        />
      )}
    </Tray.Body>
  );
};
